import { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '../../components/ui.tsx';
import { formatBytes } from '../../lib/display.ts';
import { useUploads, type UploadTask } from './UploadQueue.tsx';

/**
 * What the uploads are doing, wherever you happen to be.
 *
 * A strip across the top of the window rather than a panel inside the Drive,
 * because the point of moving uploads out of the page was that you can leave
 * the page. It reports the whole queue in one line, expands to name each file
 * when there is more than one, and gets out of the way a few seconds after the
 * last one lands.
 *
 * Failures do not auto-dismiss. A file the scanner rejected is the one thing
 * here worth reading, and a bar that clears itself would take it away before
 * anyone looked up.
 */
export function UploadBar() {
  const { tasks, preparing, clearFinished, dismiss, retryFailed } = useUploads();
  const [expanded, setExpanded] = useState(false);

  /**
   * How much of the window this strip is taking, said out loud.
   *
   * The shell reserves exactly this much at the top so the strip covers
   * nothing — in an editor there is no page header under it, and the band
   * holding the workbook's name, Export and Full screen is a few pixels at
   * the very top that could not be reached while an upload ran.
   *
   * Measured rather than assumed: the strip grows when the list of files is
   * opened, and a fixed number here would be wrong the moment it did.
   */
  const watcher = useRef<ResizeObserver | null>(null);
  const strip = useCallback((node: HTMLDivElement | null) => {
    const root = document.documentElement;
    watcher.current?.disconnect();
    watcher.current = null;

    // No strip, no space: the ref is called with null the moment it goes.
    if (!node) {
      root.style.removeProperty('--aq-upload-bar');
      return;
    }

    const measure = () => root.style.setProperty('--aq-upload-bar', `${node.offsetHeight}px`);
    measure();
    watcher.current = new ResizeObserver(measure);
    watcher.current.observe(node);
  }, []);

  // And nothing left behind if the whole bar unmounts with the page.
  useEffect(
    () => () => {
      watcher.current?.disconnect();
      document.documentElement.style.removeProperty('--aq-upload-bar');
    },
    [],
  );

  const active = tasks.filter((task) => task.status === 'uploading' || task.status === 'queued');
  const processing = tasks.filter((task) => task.status === 'processing');
  const failed = tasks.filter((task) => task.status === 'error');
  const working = active.length + processing.length;
  // Files whose bytes were already filed somewhere. Worth one line and never
  // worth stopping for — two copies of a scan in two folders is ordinary.
  const duplicates = tasks.filter((task) => task.duplicateOf).length;

  // Clear itself once everything has landed, so a finished transfer does not
  // sit at the top of the window for the rest of the session.
  useEffect(() => {
    if (tasks.length === 0 || working > 0 || failed.length > 0) return;
    const timer = setTimeout(clearFinished, 4000);
    return () => clearTimeout(timer);
  }, [tasks.length, working, failed.length, clearFinished]);

  /**
   * The preparation phase, before there is anything to measure.
   *
   * Shown as its own card so something appears the instant a folder is
   * chosen. Until this existed the screen stayed blank while the folders
   * were made and the names checked, which on a large folder is minutes.
   */
  if (tasks.length === 0 && preparing) {
    const pct = preparing.total > 0 ? Math.round((preparing.done / preparing.total) * 100) : 0;
    return (
      <div className="fixed bottom-4 right-4 z-[90] w-80 overflow-hidden rounded-lg border border-border-strong bg-surface shadow-xl">
        <div className="flex items-center justify-between bg-[var(--surface-sunken)] px-3 py-2">
          <span className="text-body-sm font-medium text-ink">Preparing upload</span>
          <span className="text-micro tabular-nums text-muted">
            {preparing.done} of {preparing.total}
          </span>
        </div>
        <div className="px-3 py-2">
          <p className="truncate text-micro text-muted">
            {preparing.label ?? 'Checking what is already filed here…'}
          </p>
          <div className="mt-2 h-1 overflow-hidden rounded bg-[var(--blue-50)]">
            <div
              className={cx(
                'h-full bg-[var(--blue-600)]',
                preparing.done === 0
                  ? 'w-1/3 animate-[aq-slide_1.1s_ease-in-out_infinite]'
                  : 'transition-fast',
              )}
              style={preparing.done === 0 ? undefined : { width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) return null;

  // Measured in bytes, not files: ten receipts and one 60 MB scan are not
  // eleven equal steps, and a bar that treats them as such jumps.
  //
  // Rejected files are left out of both sides. They have usually sent every
  // byte before the server refuses them, so counting them made a bar that was
  // mostly full before the real transfer had properly started.
  const counted = tasks.filter((task) => task.status !== 'error');
  const totalBytes = counted.reduce((sum, task) => sum + task.total, 0);
  const sentBytes = counted.reduce(
    (sum, task) => sum + (task.status === 'done' ? task.total : task.loaded),
    0,
  );
  const percent = totalBytes > 0 ? Math.round((sentBytes / totalBytes) * 100) : 0;

  // Every byte is up but the server is still scanning: there is no honest
  // number for that, so the bar shows movement instead of a stuck 100%.
  const indeterminate = working > 0 && active.length === 0;

  return (
    <div
      ref={strip}
      className="fixed inset-x-0 top-0 z-[90] border-b border-border-strong bg-surface shadow-md"
    >
      <div className="relative h-1 w-full overflow-hidden bg-[var(--blue-50)]">
        <div
          className={cx(
            'h-full bg-[var(--blue-600)]',
            indeterminate
              ? 'w-1/3 animate-[aq-slide_1.1s_ease-in-out_infinite]'
              : 'transition-fast',
          )}
          style={indeterminate ? undefined : { width: `${percent}%` }}
        />
      </div>

      <div className="flex items-center gap-3 px-4 py-2">
        <span className="text-body-sm text-ink">
          {summarise(tasks, working, failed.length, duplicates)}
        </span>

        {working > 0 && !indeterminate ? (
          <span className="text-micro tabular-nums text-muted">
            {formatBytes(sentBytes)} of {formatBytes(totalBytes)} · {percent}%
          </span>
        ) : null}

        <div className="ml-auto flex items-center gap-2">
          {/*
            Also for a single file that turned out to be a duplicate. The note
            saying which file already holds those bytes lives on the row, and
            the row lives behind this button — so with the button drawn only
            for a queue of two or more, the one case where somebody uploads a
            file they already have had nothing to open and nothing to read.
          */}
          {tasks.length > 1 || duplicates > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              className="rounded px-2 py-1 text-micro text-blue-700 transition-fast hover:bg-blue-50"
            >
              {expanded ? 'Hide' : tasks.length > 1 ? `Details (${tasks.length})` : 'Details'}
            </button>
          ) : null}

          {failed.length > 0 && working === 0 ? (
            <button
              type="button"
              onClick={retryFailed}
              className="rounded px-2 py-1 text-micro font-medium text-blue-700 transition-fast hover:bg-blue-50"
            >
              Retry {failed.length === 1 ? 'the failed file' : `${failed.length} failed files`}
            </button>
          ) : null}

          {working === 0 ? (
            <button
              type="button"
              onClick={clearFinished}
              className="rounded px-2 py-1 text-micro text-muted transition-fast hover:bg-sunken"
            >
              Dismiss
            </button>
          ) : null}
        </div>
      </div>

      {expanded ? (
        <ul className="max-h-56 overflow-y-auto border-t border-border">
          {tasks.map((task) => (
            <li
              key={task.id}
              className="flex items-center gap-3 border-b border-border px-4 py-1.5 last:border-0"
            >
              <StatusDot status={task.status} />
              <span className="min-w-0 flex-1 truncate text-body-sm text-ink" title={task.filename}>
                {task.filename}
                {/*
                  The same bytes are already filed somewhere. Said on the row
                  rather than in a dialog: it is worth knowing and never worth
                  stopping for — two copies of a scan in two folders is an
                  ordinary thing to want.
                */}
                {task.duplicateOf ? (
                  <span className="block truncate text-micro font-normal text-muted">
                    Same as “{task.duplicateOf.name}”,{' '}
                    {task.duplicateOf.trashed ? 'sent before' : 'already in the Drive'}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-micro text-muted">{describe(task)}</span>
              {task.status === 'error' ? (
                <button
                  type="button"
                  onClick={() => dismiss(task.id)}
                  className="shrink-0 rounded px-1.5 py-0.5 text-micro text-muted hover:bg-sunken"
                >
                  ✕
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {/* Not in the Tailwind config: it exists only for this bar. */}
      <style>{`@keyframes aq-slide { 0% { transform: translateX(-100%) } 100% { transform: translateX(400%) } }`}</style>
    </div>
  );
}

function summarise(
  tasks: readonly UploadTask[],
  working: number,
  failedCount: number,
  duplicates: number,
): string {
  if (working > 0) {
    const current = tasks.find(
      (task) => task.status === 'uploading' || task.status === 'processing',
    );
    const label =
      current?.status === 'processing'
        ? waitingFor(current.filename).replace('…', '')
        : 'Uploading';
    const position = tasks.length - working + 1;
    return tasks.length > 1
      ? `${label} ${position} of ${tasks.length} — ${current?.filename ?? ''}`
      : `${label} ${current?.filename ?? ''}`;
  }

  if (failedCount > 0) {
    const ok = tasks.length - failedCount;
    return ok > 0
      ? `${ok} uploaded, ${failedCount} could not be`
      : failedCount === 1
        ? 'That file could not be uploaded'
        : `${failedCount} files could not be uploaded`;
  }

  /*
   * Said on the bar itself, not only on the row behind the expander. The
   * whole point of checking the bytes is to tell somebody they already have
   * this file, and a line nobody opens tells them nothing.
   */
  if (tasks.length === 1) {
    return duplicates === 1 ? 'Uploaded — you already had this file' : 'Uploaded';
  }

  return duplicates === 0
    ? `${tasks.length} files uploaded`
    : `${tasks.length} files uploaded, ${duplicates} you already had`;
}

/**
 * What the server is doing with a file that has finished arriving.
 *
 * It scans everything, and a workbook or a Word file is then opened — which
 * for a 300-tab workbook is the best part of a minute of silence. Reported as
 * "Scanning…" throughout, that silence reads as a stall: the client refreshed
 * the page part-way through and uploaded the same workbook again, which is
 * how one upload turned into two entries in the Drive. Naming the slow part
 * costs nothing and is the truth.
 */
function waitingFor(filename: string): string {
  if (/\.(xlsx|xlsm|xls|csv)$/i.test(filename)) return 'Opening as a workbook…';
  if (/\.docx$/i.test(filename)) return 'Opening as a document…';
  return 'Scanning…';
}

function describe(task: UploadTask): string {
  switch (task.status) {
    case 'queued':
      return 'Waiting';
    case 'uploading':
      return task.total > 0 ? `${Math.round((task.loaded / task.total) * 100)}%` : 'Sending';
    case 'processing':
      return waitingFor(task.filename);
    case 'done':
      return task.duplicateOf ? 'Done · duplicate' : 'Done';
    case 'skipped':
      return 'Skipped';
    default:
      return task.message ?? 'Failed';
  }
}

function StatusDot({ status }: { status: UploadTask['status'] }) {
  return (
    <span
      aria-hidden
      className={cx(
        'h-2 w-2 shrink-0 rounded-full',
        status === 'done'
          ? 'bg-[var(--success)]'
          : status === 'error'
            ? 'bg-[var(--danger)]'
            : status === 'queued' || status === 'skipped'
              ? 'bg-[var(--border-strong)]'
              : 'bg-[var(--blue-600)]',
      )}
    />
  );
}
